// Calling V2 — provider/model/voice presets for the admin template editor.
//
// Each MODELS_BY_PROVIDER map filters the model dropdown to only entries that
// actually belong to the selected provider. Picking "__custom__" reveals a
// paired text input so admins can drop in any model string the runtime
// supports. Values are valid as of June 2026; add to the lists as vendors
// release new models.

// LLM provider is OpenAI-only today (matches the Calling Runtime's default
// LLM and what we're currently certified against). To add another provider
// later, append to LLM_PROVIDERS + add the matching key in
// LLM_MODELS_BY_PROVIDER below; the editor picks it up automatically.
//
// Source: https://platform.openai.com/docs/models
export const LLM_PROVIDERS = [
  "openai",
];

export const LLM_MODELS_BY_PROVIDER = {
  openai: [
    "gpt-5.5",
    "gpt-5.4",
    "gpt-5.4-mini",
    "gpt-5.4-nano",
    "gpt-5.3-codex",
    "gpt-4.1",
    "gpt-4.1-mini",
    "gpt-4o",
    "gpt-4o-mini",
    "o3",
    "o3-mini",
    "o3-pro",
  ],
};

// STT providers are restricted to Deepgram + OpenAI today. Deepgram is the
// runtime's default; OpenAI is the alternate covered for English/whisper
// workloads. To add another provider later, append to STT_PROVIDERS + the
// matching key in STT_MODELS_BY_PROVIDER below.
//
// Sources:
//   https://developers.deepgram.com/docs/models-languages-overview
//   https://platform.openai.com/docs/models (whisper / gpt-4o-transcribe)
export const STT_PROVIDERS = [
  "deepgram",
  "openai",
];

export const STT_MODELS_BY_PROVIDER = {
  deepgram: [
    "nova-3",
    "nova-3-general",
    "nova-3-medical",
    "nova-2",
    "nova-2-conversationalai",
    "nova-2-phonecall",
    "nova-2-meeting",
    "nova-2-finance",
    "enhanced",
    "base",
  ],
  openai: [
    "whisper-1",
    "gpt-4o-transcribe",
    "gpt-4o-mini-transcribe",
    "gpt-realtime-whisper",
  ],
};

export const STT_LANGUAGES = [
  "en", "en-US", "en-GB", "en-AU", "en-IN",
  "es", "es-ES", "es-MX", "es-419",
  "fr", "fr-FR", "fr-CA",
  "de", "it", "pt", "pt-BR", "nl", "pl", "sv",
  "ja", "ko", "zh", "zh-CN", "zh-TW",
  "hi", "ar", "ru", "tr", "id", "th", "vi",
  "multi",
];

// TTS providers are restricted to OpenAI + ElevenLabs today. OpenAI is the
// runtime's default; ElevenLabs covers the higher-fidelity voice library
// customers ask for. To add another provider later, append to TTS_PROVIDERS
// + the matching key in TTS_VOICES_BY_PROVIDER below.
//
// Sources:
//   https://platform.openai.com/docs/models (TTS / Realtime voices)
//   https://elevenlabs.io/docs/eleven-creative/voices/voice-library
export const TTS_PROVIDERS = [
  "openai",
  "elevenlabs",
];

export const TTS_VOICES_BY_PROVIDER = {
  openai: [
    "alloy", "echo", "fable", "onyx", "nova", "shimmer",
    "ash", "ballad", "coral", "sage", "verse",
    "cedar", "marin",
  ],
  elevenlabs: [
    "21m00Tcm4TlvDq8ikWAM",  // Rachel
    "AZnzlk1XvdvUeBnXmlld",  // Domi
    "EXAVITQu4vr4xnSDxMaL",  // Bella
    "ErXwobaYiN019PkySvjV",  // Antoni
    "MF3mGyEYCl7XYWbV9V6O",  // Elli
    "TxGEqnHWrfWFTfGW9XjX",  // Josh
    "VR6AewLTigWG4xSOukaG",  // Arnold
    "pNInz6obpgDQGcFmaJgB",  // Adam
    "yoZ06aMxZJJ28mfd3POQ",  // Sam
  ],
};

// Realtime API is OpenAI-only (their gpt-realtime family of speech-to-speech
// models). Voices below match the May 2026 lineup: two new realtime-exclusive
// voices (cedar, marin) plus the eight legacy voices retro-fitted with the
// realtime improvements (alloy, echo, fable, onyx, nova, shimmer) and the
// extended set from the GPT-4o family (ash, ballad, coral, sage, verse).
//
// Source: https://platform.openai.com/docs/models/gpt-realtime
//         https://openai.com/index/introducing-gpt-realtime/
//         https://openai.com/index/advancing-voice-intelligence-with-new-models-in-the-api/
export const REALTIME_PROVIDERS = [
  "openai",
];

export const REALTIME_MODELS_BY_PROVIDER = {
  openai: [
    "gpt-realtime-2",           // May 2026 — GPT-5-class reasoning
    "gpt-realtime",             // Original GA model
    "gpt-realtime-mini",        // Lower latency / lower cost
    "gpt-realtime-translate",   // Live translation across 70+ languages
    "gpt-4o-realtime-preview",  // Legacy preview (Dec 2024)
  ],
};

export const REALTIME_VOICES_BY_PROVIDER = {
  openai: [
    "marin",     // New May 2026, realtime-exclusive
    "cedar",     // New May 2026, realtime-exclusive
    "alloy",
    "echo",
    "fable",
    "onyx",
    "nova",
    "shimmer",
    "ash",
    "ballad",
    "coral",
    "sage",
    "verse",
  ],
};

export const CUSTOM_SENTINEL = "__custom__";
